import { Button, ClubIcon, PageTitle } from '@/components';
import { useGetClubs, useGetCompany } from '@/services';
import { Crown, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AddClubModal } from '../components/AddClubModal';
import { UnlinkClubModal } from '../components/UnlinkClubModal';
import { useUnlinkClub } from '@/services/mutations/company';

export const CompanyClub = () => {
	const { id } = useParams<{ id: string }>();
	const { data: company } = useGetCompany(id || '', {
		refetchOnWindowFocus: true,
	});
	const [isAddClubModalOpen, setIsAddClubModalOpen] = useState(false);
	const { data: clubsData } = useGetClubs();
	const clubsById = useMemo(() => {
		if (!clubsData) return {} as Record<string, Clubs.Club>;

		// `useGetClubs` pode retornar Clubs.Club[] ou GetClubsResponse
		const clubsArray: Clubs.Club[] = Array.isArray(clubsData)
			? clubsData
			: (clubsData as Clubs.GetClubsResponse).data ?? [];

		const map: Record<string, Clubs.Club> = {};
		clubsArray.forEach((c) => {
			map[c.id] = c;
		});
		return map;
	}, [clubsData]);

	const [clubToUnlink, setClubToUnlink] =
		useState<Companies.CompanyClubDetail | null>(null);

	const { mutateAsync: unlinkClub, isPending: isUnlinking } = useUnlinkClub(
		id || '',
		() => setClubToUnlink(null),
	);

	const subscriptions = company?.company_plan_subscription ?? [];
	const activeSubscription = subscriptions.find((s) => s.status === 'ACTIVE');
	const subscription = activeSubscription ?? subscriptions[0];

	const plan = subscription?.company_plan;
	const subsidyPerEmployee = plan
		? Number(plan.subsidy_per_employee || 0)
		: 0;
	return (
		<section className="flex animate-fade-in flex-col gap-6">
			<PageTitle text="Clubes da empresa" />
			{/* Clubes oferecidos */}
			<div className="space-y-4">
				<div className="flex items-center justify-between">
					<div className="space-y-1">
						<h3 className="text-lg font-black text-stone-900">
							Clubes oferecidos
						</h3>
						<p className="text-sm text-stone-400">
							{company?.company_club?.length ?? 0} vinculado
							{(company?.company_club?.length ?? 0) !== 1
								? 's'
								: ''}
						</p>
					</div>
					<Button
						text="Adicionar clube"
						className="rounded-full bg-black px-6 py-2.5 font-bold shadow-md hover:bg-neutral-800"
						startIcon={
							<Plus className="size-5 stroke-[3] text-white" />
						}
						onClick={() => setIsAddClubModalOpen(true)}
					/>
				</div>

				<div className="space-y-2">
					{company?.company_club?.length ? (
						company?.company_club.map((cc) => {
							const clubFromQuery = clubsById[cc.club_id];
							const club = (clubFromQuery || cc.club) as
								| (Clubs.Club &
										Partial<
											Pick<
												Clubs.Club,
												'price' | 'currency'
											>
										>)
								| undefined;

							return (
								<div
									key={cc.id}
									className="group flex items-center justify-between rounded-2xl border border-stone-100 bg-white p-4 shadow-sm transition-all hover:border-stone-200"
								>
									<div className="flex items-center gap-4">
										{club && (
											<ClubIcon club={club} size="md" />
										)}
										<div className="flex flex-col">
											<span className="text-sm font-bold leading-tight text-stone-900">
												{club?.name}
											</span>
											<span className="text-[10px] font-bold uppercase tracking-tight text-stone-400">
												{club?.shelf ?? 'B2B2C'}
											</span>
											{/* Preço agora é exibido apenas na coluna da direita, ao lado do ícone de lixeira */}
										</div>
									</div>
									<div className="flex items-center gap-6">
										<div className="hidden text-right text-sm font-black text-stone-900 sm:flex sm:flex-col">
											{(() => {
												const rawPrice = club?.price;
												if (!rawPrice) return null;

												const base =
													parseFloat(rawPrice);
												const hasSubsidy =
													subsidyPerEmployee > 0 &&
													base > 0;
												const effectiveSubsidy =
													hasSubsidy
														? Math.min(
																subsidyPerEmployee,
																base,
														  )
														: 0;
												const employeeCost = Math.max(
													0,
													base - effectiveSubsidy,
												);

												if (effectiveSubsidy > 0) {
													return (
														<>
															<span className="text-xs font-semibold text-stone-400 line-through">
																{club?.currency}{' '}
																{base.toLocaleString(
																	'pt-BR',
																	{
																		minimumFractionDigits: 2,
																	},
																)}
															</span>
															<span>
																{club?.currency}{' '}
																{employeeCost.toLocaleString(
																	'pt-BR',
																	{
																		minimumFractionDigits: 2,
																	},
																)}
															</span>
														</>
													);
												}

												return (
													<span>
														{club?.currency}{' '}
														{base.toLocaleString(
															'pt-BR',
															{
																minimumFractionDigits: 2,
															},
														)}
													</span>
												);
											})()}
										</div>

										<div className="flex gap-1">
											<button
												onClick={(e) => {
													e.stopPropagation();
													setClubToUnlink(cc);
												}}
												className="rounded-lg p-2 text-stone-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
												title="Desvincular"
											>
												<Trash2 size={16} />
											</button>
										</div>
									</div>
								</div>
							);
						})
					) : (
						<div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-stone-200 bg-stone-50/50 py-16 text-center">
							<div className="mb-3 rounded-full bg-white p-4 text-stone-300 shadow-sm">
								<Crown size={28} />
							</div>
							<p className="text-sm font-bold text-stone-600">
								Nenhum clube vinculado
							</p>
							<p className="mt-1 text-xs text-stone-400">
								Adicione clubes B2B2C oferecidos aos
								colaboradores desta empresa.
							</p>
							<Button
								text="Adicionar clube"
								variant="outline"
								className="mt-4 rounded-full border-stone-300 px-5 py-2 font-bold"
								startIcon={
									<Plus className="size-4 stroke-[3]" />
								}
								onClick={() => setIsAddClubModalOpen(true)}
							/>
						</div>
					)}
				</div>
			</div>
			{company && (
				<>
					<AddClubModal
						isOpen={isAddClubModalOpen}
						onClose={() => setIsAddClubModalOpen(false)}
						companyId={company.id}
						linkedClubIds={
							company.company_club?.map((cc) => cc.club_id) ?? []
						}
					/>
					<UnlinkClubModal
						isOpen={!!clubToUnlink}
						onClose={() => setClubToUnlink(null)}
						onConfirm={async () => {
							if (clubToUnlink) {
								await unlinkClub(clubToUnlink.club_id);
							}
						}}
						clubName={clubToUnlink?.club?.name ?? ''}
						isUnlinking={isUnlinking}
					/>
				</>
			)}
		</section>
	);
};
